import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import { fetchOwnCompanyRemuneration } from "../api/companyFinance";
import { fetchCompany, fetchMyCompany } from "../api/companyWorkspace";
import {
  formatCompanyFinanceDate,
  formatCompanyFinanceMoney,
  isHistoricalMembership,
  listResults,
  mapCompanyFinanceError,
  membershipGenerationOf,
  obligationStatusLabel,
} from "../lib/companyFinanceLabels";
import { isActiveMember } from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function amountsDiffer(a, b) {
  if (a == null || b == null) return false;
  return String(a) !== String(b);
}

function RemunerationCard({ row }) {
  const showEffective = amountsDiffer(row.amount, row.effective_amount);
  const partial =
    row.status === "open" &&
    Number(row.settled_amount) > 0 &&
    Number(row.outstanding) > 0;
  return (
    <article
      className={[
        "feature-company-finance__record",
        partial ? "feature-company-finance__record--partial" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={`company-remuneration-row-${row.obligation_id}`}
      data-status={row.status}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Зобовʼязання #{row.obligation_id}
        </h3>
        <p className="feature-company-finance__record-meta">
          {obligationStatusLabel(row.status)}
          {row.due_on ? ` · до ${formatCompanyFinanceDate(row.due_on)}` : ""}
        </p>
        {row.deal_reference ? (
          <p
            className="feature-company-finance__deal-reference"
            data-testid={`company-remuneration-deal-ref-${row.obligation_id}`}
          >
            Угода: {row.deal_reference}
          </p>
        ) : null}
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>{showEffective ? "Початково" : "Сума"}</dt>
          <dd>{formatCompanyFinanceMoney(row.amount, row.currency)}</dd>
        </div>
        {showEffective ? (
          <div>
            <dt>Після коригувань</dt>
            <dd>{formatCompanyFinanceMoney(row.effective_amount, row.currency)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Профінансовано</dt>
          <dd>{formatCompanyFinanceMoney(row.funded_amount, row.currency)}</dd>
        </div>
        <div>
          <dt>Сплачено</dt>
          <dd>{formatCompanyFinanceMoney(row.settled_amount, row.currency)}</dd>
        </div>
        <div>
          <dt>Залишок</dt>
          <dd>{formatCompanyFinanceMoney(row.outstanding, row.currency)}</dd>
        </div>
      </dl>
    </article>
  );
}

function CompanyMyRemunerationContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    setState("loading");
    setError(null);

    Promise.all([
      fetchCompany(companyId).catch((err) => {
        // Finder-safe / detail may 404 for true strangers; still try remuneration.
        if (err?.response?.status === 404) return null;
        throw err;
      }),
      fetchMyCompany().catch(() => null),
      fetchOwnCompanyRemuneration(companyId),
    ])
      .then(([companyData, mine, remunerationPayload]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const mineMatches =
          mine?.company?.id != null &&
          sameCompanyId(mine.company.id, companyId)
            ? mine.membership
            : null;
        const effectiveMembership =
          mineMatches ||
          companyData?.my_membership ||
          null;
        setCompany(
          companyData ||
            (mineMatches
              ? mine.company
              : { id: Number(companyId), name: "Компанія", status: "active" }),
        );
        setMembership(effectiveMembership);
        setAuthorityActorId(actorId);
        setRows(listResults(remunerationPayload));
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const mapped = mapCompanyFinanceError(err);
        setCompany(null);
        setMembership(null);
        setAuthorityActorId(null);
        setRows([]);
        if (
          err?.response?.status === 404 ||
          mapped.code === "not_company_record"
        ) {
          setState("not-found");
          return;
        }
        if (err?.response?.status === 403) {
          setError(mapped.message || "У вас немає доступу.");
          setState("forbidden");
          return;
        }
        setError(mapped.message || messageFromError(err));
        setState("error");
      });
  }, [actorId, companyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: null,
      bindMembershipGeneration: true,
    });
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRows([]);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!company) return;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: membershipGenerationOf(company, membership),
      bindMembershipGeneration: true,
    });
  }, [company, membership, companyId, actorId, scopeGate]);

  if (state === "loading") {
    return (
      <div
        className="feature-company-workspace__state"
        data-testid="company-remuneration-loading"
      >
        <Spinner size="lg" label="Завантаження винагороди" />
      </div>
    );
  }

  if (state === "not-found") {
    return (
      <div
        className="feature-company-workspace__state"
        data-testid="company-remuneration-not-found"
      >
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Запис недоступний"
          description="Інформація про винагороду недоступна."
          size="md"
        />
      </div>
    );
  }

  if (state === "forbidden") {
    return (
      <div
        className="feature-company-workspace__state"
        data-testid="company-remuneration-forbidden"
      >
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description={error || "У вас немає доступу."}
          size="md"
        />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        className="feature-company-workspace__state"
        data-testid="company-remuneration-error"
      >
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Помилка завантаження"
          description={error || "Спробуйте оновити сторінку."}
          size="md"
        />
        <button type="button" className="action-btn" onClick={load}>
          Повторити
        </button>
      </div>
    );
  }

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  if (!companyMatchesRoute || !actorMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження винагороди" />
      </div>
    );
  }

  const historical = isHistoricalMembership(company, membership);
  const activeMember = isActiveMember(company, membership);
  // Suspended/ended: never mount Company workspace navigation.
  const shellCompanyId = activeMember && !historical ? company.id : null;
  const shellCompany = activeMember && !historical ? company : null;
  const shellMembership = activeMember && !historical ? membership : null;

  return (
    <section
      className="clients-page feature-company-workspace feature-company-finance"
      id="Csection"
      tabIndex="-1"
      data-testid="company-remuneration-screen"
      data-membership-status={
        membership?.status || company?.my_membership?.status || "unknown"
      }
      data-company-nav={shellCompanyId ? "mounted" : "absent"}
    >
      <div className="container">
        <CompanyPageHeader
          title="Моя винагорода"
          subtitle={
            historical
              ? "Ваші зобовʼязання як співробітника. Доступ до робочої оболонки компанії не відновлюється."
              : "Ваші зобовʼязання як співробітника цієї компанії"
          }
          company={shellCompany}
          membership={shellMembership}
          companyId={shellCompanyId}
          activeNav="my-remuneration"
        />

        {historical ? (
          <div
            className="feature-company-workspace__banner"
            role="status"
            data-testid="company-remuneration-historical-banner"
          >
            Членство{" "}
            {membership?.status === "ended" ? "завершено" : "призупинено"}.
            Показано лише вашу винагороду — без навігації компанії, клієнтів,
            обʼєктів чи корпоративних книг.
          </div>
        ) : null}

        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-wallet"
            title="Немає записів винагороди"
            description="Коли зʼявляться зобовʼязання перед вами, вони будуть тут."
            size="md"
          />
        ) : (
          <div
            className="feature-company-finance__list"
            data-testid="company-remuneration-list"
          >
            {rows.map((row) => (
              <RemunerationCard key={row.obligation_id} row={row} />
            ))}
          </div>
        )}

        {!historical && shellCompanyId ? (
          <p className="feature-company-finance__footer-link">
            <Link to={`/companies/${shellCompanyId}`}>Повернутися до профілю</Link>
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default CompanyMyRemunerationContent;
